import { useCallback, useEffect, useState } from "react";
import { api } from "../api/apiClient";
import type { BugReportListItem } from "../api/types";

/**
 * G-72 — where the owner reads what the shops hit.
 *
 * SEPARATE FROM THE FEATURE BACKLOG, deliberately, and the reasoning is worth
 * keeping. A ticket on that board is something a person decided to write and
 * wants worked on. A bug report is EVIDENCE, and two of its properties do not
 * belong on a board you triage by hand:
 *   - the AUTOMATIC ones arrive on their own, and one bad deploy can file fifty
 *     in an afternoon, which would bury the backlog;
 *   - every report expires after 90 days, because a kiosk screenshot carries
 *     customer names and delivery addresses, and `AppTicket` has no expiry.
 * The bridge between them is a person reading this page and deciding something
 * is worth a ticket.
 *
 * NEWEST-FIRST, UNRESOLVED-FIRST, and everything on one card. The owner is
 * triaging, not browsing: the question is always "is this the same thing I
 * already saw", which needs the screen, the shop and the error visible without
 * opening anything.
 */

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-ES", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

/** Days left before the sweep deletes this row and its screenshot. */
function daysLeft(expiresAt: string): number | null {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

function ScreenshotThumb({ id }: { id: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    void api
      .bugReportScreenshotUrl(id)
      .then((next) => {
        if (cancelled) {
          // Arrived after the card unmounted. Revoke here or the blob leaks for
          // the life of the tab — and these are full-screen PNGs.
          if (next) URL.revokeObjectURL(next);
          return;
        }
        created = next;
        if (next) setUrl(next);
        else setFailed(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [id]);

  if (failed) return <p className="muted">Screenshot could not be loaded.</p>;
  if (!url) return <p className="muted">Loading screenshot…</p>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="bug-shot-link">
      <img src={url} alt="The screen as the reporter saw it" className="bug-shot" />
    </a>
  );
}

export function BugReportsPage() {
  const [reports, setReports] = useState<BugReportListItem[]>([]);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReports(await api.listBugReports(includeResolved));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load bug reports");
    } finally {
      setLoading(false);
    }
  }, [includeResolved]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleResolved = async (report: BugReportListItem) => {
    const next = report.resolvedAt === null;
    try {
      const updated = await api.setBugReportResolved(report.id, next);
      setReports((prev) =>
        // Kept in place rather than refetching: the list is sorted with
        // unresolved first, so a refetch would make the row jump away from
        // under the cursor mid-triage.
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not update that report");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <h1>Bug reports</h1>
        <p className="muted">
          What the shops hit, with the screen and the log attached. Reports are
          deleted automatically after 90 days — they contain customer details.
        </p>
      </header>

      <div className="card bug-toolbar">
        <label className="bug-toolbar-toggle">
          <input
            type="checkbox"
            checked={includeResolved}
            onChange={(e) => setIncludeResolved(e.target.checked)}
          />
          <span>Show resolved</span>
        </label>
        <button type="button" className="button ghost" onClick={() => void load()}>
          Refresh
        </button>
      </div>

      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p className="muted">Loading…</p> : null}

      {!loading && reports.length === 0 ? (
        <div className="card">
          <p className="muted">
            {includeResolved
              ? "No reports yet."
              : "Nothing outstanding. Tick “Show resolved” to see what has been dealt with."}
          </p>
        </div>
      ) : null}

      {reports.map((report) => {
        const isOpen = expanded === report.id;
        const remaining = daysLeft(report.expiresAt);
        return (
          <article
            key={report.id}
            className={report.resolvedAt ? "card bug-card is-resolved" : "card bug-card"}
          >
            <div className="bug-card-head">
              <div>
                {/* An automatic report has no message, so the kind IS the
                    headline — otherwise the card would open with a blank. */}
                <strong>
                  {report.kind === "AUTOMATIC"
                    ? "Crash (reported automatically)"
                    : report.message}
                </strong>
                <p className="muted bug-card-meta">
                  {report.location?.name ?? "No shop"}
                  {" · "}
                  {report.reportedBy?.fullName ?? "Unknown user"}
                  {report.screen ? ` · ${report.screen}` : ""}
                  {" · "}
                  {formatWhen(report.occurredAt)}
                </p>
              </div>
              <button
                type="button"
                className="button ghost"
                onClick={() => void toggleResolved(report)}
              >
                {report.resolvedAt ? "Reopen" : "Mark done"}
              </button>
            </div>

            {report.errorMessage ? (
              <p className="bug-card-error">{report.errorMessage}</p>
            ) : null}

            {/* The device's own clock said one time and the server received it
                at another. Only worth saying when they differ enough to matter —
                which means the tablet queued it while the line was down. */}
            {Math.abs(
              new Date(report.createdAt).getTime() - new Date(report.occurredAt).getTime(),
            ) >
            5 * 60 * 1000 ? (
              <p className="muted bug-card-meta">
                Held on the tablet — arrived {formatWhen(report.createdAt)}
              </p>
            ) : null}

            <button
              type="button"
              className="button ghost"
              onClick={() => setExpanded(isOpen ? null : report.id)}
            >
              {isOpen ? "Hide details" : "Show screen and log"}
            </button>

            {isOpen ? (
              <div className="bug-card-detail">
                {report.screenshotPath ? (
                  <ScreenshotThumb id={report.id} />
                ) : (
                  <p className="muted">No screenshot — filed from a browser, or the capture failed.</p>
                )}

                {report.errorStack ? (
                  <details>
                    <summary>Stack</summary>
                    <pre className="bug-pre">{report.errorStack}</pre>
                  </details>
                ) : null}

                {report.consoleLog ? (
                  <details>
                    <summary>Console</summary>
                    <pre className="bug-pre">{report.consoleLog}</pre>
                  </details>
                ) : (
                  <p className="muted">No log captured.</p>
                )}

                <p className="muted bug-card-meta">
                  {report.viewportWidth && report.viewportHeight
                    ? `${report.viewportWidth}×${report.viewportHeight} · `
                    : ""}
                  {remaining !== null ? `deleted in ${remaining} days` : ""}
                </p>
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}
